/**
 * «Bitácora», searched (the owner, 2026-10-06: «we should have full searchable histories where
 * applicable honestly»): the log of everything that was done, found by the day it was done, by
 * who did it, and by what is written in it.
 *
 * It read the latest fifteen hundred records and no further, which is a few days of a busy shop:
 * what happened the month before could not be looked at. With anything asked of it, it looks
 * through the whole log on this device. Pure, so the screen and its tests share one answer.
 */

import { fechaEnNicaragua } from '@abastece/shared';
import { dentroDe, rangoAbierto, type Periodo, type Rango } from './historial.js';
import { coincide } from './texto.js';

export interface FiltroBitacora {
  /** The box: what the record is, who did it, its number, and its own words. */
  texto: string;
  periodo: Periodo;
  desde: string;
  hasta: string;
  /** Who did it; empty for anybody. */
  quien: string;
}

export const SIN_FILTROS_BITACORA: FiltroBitacora = { texto: '', periodo: 'todo', desde: '', hasta: '', quien: '' };

/**
 * The fields of a record that are words somebody would remember: a ticket, a reason, a name, a
 * receipt. Named one by one rather than every text in it: a PIN's hash is text too, and so is a
 * cédula, and neither belongs under a search box.
 */
const CAMPOS_CON_PALABRAS = [
  'ticket',
  'motivo',
  'descripcion',
  'nombre',
  'nombreEn',
  'documento',
  'referencia',
  'telefono',
  'operadora',
  'nota',
  'notas',
  'cargo',
  'contacto',
  'direccion',
  'codigo',
] as const;

/** A record's own words, in the order its fields are named above. */
export function palabrasDelEvento(payload: unknown): string[] {
  if (!payload || typeof payload !== 'object') return [];
  const p = payload as Record<string, unknown>;
  return CAMPOS_CON_PALABRAS.map((c) => p[c]).filter((v): v is string => typeof v === 'string' && v.trim() !== '');
}

/** A record of the log, as much as the search reads of it. */
export interface EventoDeBitacora {
  actorId: string;
  clientTs: string;
  serverSeq?: number;
  payload?: unknown;
  motivoRechazo?: string;
}

/** The day a record was done, in Nicaragua. */
export const fechaDelEvento = (e: Pick<EventoDeBitacora, 'clientTs'>): string => fechaEnNicaragua(e.clientTs);

/**
 * Every condition set holds for this record. `etiqueta` is what its row calls it and `nombre` who
 * did it, as the screen says them; the box looks through those, its number as «#123», why the
 * books refused it, and its own words.
 */
export function eventoCoincide(e: EventoDeBitacora, f: FiltroBitacora, rango: Rango, etiqueta: string, nombre: string | undefined): boolean {
  if (f.quien && e.actorId !== f.quien) return false;
  if (!rangoAbierto(rango) && !dentroDe(fechaDelEvento(e), rango)) return false;
  if (!f.texto.trim()) return true;
  const texto = [etiqueta, nombre ?? '', e.serverSeq ? `#${e.serverSeq}` : '', e.motivoRechazo ?? '', ...palabrasDelEvento(e.payload)].join(' ');
  return coincide(texto, f.texto);
}

/** The conditions set besides the box. */
export function filtrosDeBitacora(f: FiltroBitacora, rango: Rango): (keyof FiltroBitacora)[] {
  const puestos: (keyof FiltroBitacora)[] = [];
  if (!rangoAbierto(rango)) puestos.push('periodo');
  if (f.quien) puestos.push('quien');
  return puestos;
}
